// @ts-nocheck
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Typography, FormControlLabel, Checkbox } from '@material-ui/core';
import { DateUtils } from '../../../../../common/utils';
import ProductCard from '../common/ProductCard';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  item: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: '0',
    },
  },
  text: {
    marginBottom: theme.spacing(2),
  },
  products: {
    paddingRight: theme.spacing(1),
    overflow: 'auto',
  },
  partnerCheckbox: {
    'align-self': 'end',
  },
}));

const getDefaultDuration = isPartnerAmbassador => (isPartnerAmbassador ? 365 : 30);

const getValidProducts = (sortedProducts, isPartnerAmbassador) =>
  _.filter(
    sortedProducts,
    product => product.buyable && !(isPartnerAmbassador && product.productGroup === 'LIVECHG')
  );

const createInitialData = (productId, duration) => ({
  id: productId,
  products: {
    [productId]: {
      id: productId,
      duration,
      features: [],
    },
  },
});

function SubscriptionProducts(props) {
  const [data, setData] = useState({});
  const [partnerAmbassador, setPartnerAmbassador] = useState(false);
  const DEFAULT_DURATION = getDefaultDuration(partnerAmbassador);
  const DURATION_ITEMS = partnerAmbassador ? [365] : [1, 7, 14, 30, 90, 365];
  const [t] = useTranslation();
  const classes = useStyles();

  const { channels, products, onChange } = props;

  const sortedProducts = useMemo(() => _.orderBy(products, 'price', 'desc'), [products]);

  const validProducts = useMemo(
    () => getValidProducts(sortedProducts, partnerAmbassador),
    [sortedProducts, partnerAmbassador]
  );

  const handlePartnerAmbassadorChange = event => {
    const checked = event.target.checked;
    const nextProductId = getValidProducts(sortedProducts, checked)[0]?.id;
    setPartnerAmbassador(checked);
    setData(
      _.isNil(nextProductId) ? {} : createInitialData(nextProductId, getDefaultDuration(checked))
    );
  };

  useEffect(() => {
    const productId = validProducts[0]?.id;
    setData(_.isNil(productId) ? {} : createInitialData(productId, DEFAULT_DURATION));
  }, [validProducts, DEFAULT_DURATION]);

  useEffect(() => {
    onChange({
      product: {
        data: data.products?.[data.id],
        partnerAmbassador: partnerAmbassador,
      },
    });
  }, [data, onChange, partnerAmbassador]);

  const updateSelectedProduct = (productId, changes = {}) => {
    setData(prevData => ({
      id: productId,
      products: {
        ...prevData.products,
        [productId]: {
          ...(prevData.products?.[productId]
            ? prevData.products[productId]
            : {
                id: productId,
                duration: DEFAULT_DURATION,
                features: [],
              }),
          ...changes,
        },
      },
    }));
  };

  const renderProducts = () =>
    validProducts.map(product => {
      const isSelected = product.id === data.id;
      const isLive = product.productGroup === 'LIVECHG';

      const icon = isSelected ? 'check' : 'arrow_forward';
      const color = isLive ? 'orange' : 'blue';

      const selectedDuration = _.defaultTo(data.products?.[product.id]?.duration, DEFAULT_DURATION);
      const expirationDate = DateUtils.getFutureDate(selectedDuration);

      const duration = {
        value: selectedDuration,
        items: DURATION_ITEMS,
        onChange: value => {
          updateSelectedProduct(product.id, {
            duration: value,
          });
        },
      };

      let live;
      if (isLive) {
        const selectedChannels = _.defaultTo(data.products?.[product.id]?.features, []);
        live = {
          value: selectedChannels.map(channel => channel.id),
          items: channels,
          onChange: ({ value, checked }) => {
            if (!checked) {
              updateSelectedProduct(product.id, {
                features: selectedChannels.filter(channel => channel.id !== _.toNumber(value)),
              });
            } else {
              updateSelectedProduct(product.id, {
                features: selectedChannels.concat({ id: _.toNumber(value) }),
              });
            }
          },
        };
      }

      return (
        <Box key={product.id} className={classes.item}>
          <ProductCard
            productId={product.id}
            title={product.title}
            subtitle={product.productGroup}
            icon={icon}
            color={color}
            live={live}
            duration={duration}
            expirationDate={expirationDate}
            selected={isSelected}
            onProductSelected={value => updateSelectedProduct(_.toNumber(value))}
            durationReadonly={partnerAmbassador}
          />
        </Box>
      );
    });

  return (
    <Box className={classes.container}>
      <Typography variant="caption" className={classes.text}>
        {`* ${t('subscription.subscriptionAssignProductInfo')}`}
      </Typography>
      <FormControlLabel
        className={classes.partnerCheckbox}
        control={
          <Checkbox
            id={`ProductCard-partnerAmbassador`}
            color="primary"
            onChange={handlePartnerAmbassadorChange}
          />
        }
        label={t('subscription.partnerAmbassador')}
      />
      <Box className={classes.products}>{renderProducts()}</Box>
    </Box>
  );
}

SubscriptionProducts.propTypes = {
  channels: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SubscriptionProducts;
